import React, { useEffect, useState } from 'react';
import { X } from 'lucide-react';
import { fetchProducts, createOrder } from '../../lib/api';

/**
 * Mock checkout modal. Pass `attributionRef` (post id or creator code) to attribute the order.
 * Used both from the Sevagram feed and standalone in Shop.
 */
const OrderModal = ({
  attributionRef,
  initialProductSlugs = [],
  creatorHandle,
  onClose,
}) => {
  const [products, setProducts] = useState([]);
  const [selectedSlug, setSelectedSlug] = useState(initialProductSlugs[0] || '');
  const [qty, setQty] = useState(1);
  const [email, setEmail] = useState('');
  const [refInput, setRefInput] = useState(attributionRef || '');
  const [state, setState] = useState({ loading: false, result: null, err: '' });

  useEffect(() => {
    fetchProducts()
      .then((p) => {
        setProducts(p);
        if (!selectedSlug && p.length) setSelectedSlug(p[0].slug);
      })
      .catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const product = products.find((p) => p.slug === selectedSlug);
  const subtotal = product ? product.price_usd * qty : 0;

  const onSubmit = async (e) => {
    e.preventDefault();
    setState({ loading: true, result: null, err: '' });
    try {
      const res = await createOrder({
        buyer_email: email,
        items: [{ product_slug: selectedSlug, quantity: qty }],
        ref: refInput || undefined,
      });
      setState({ loading: false, result: res, err: '' });
    } catch (err) {
      const detail = err?.response?.data?.detail || 'Order failed. Please try again.';
      setState({
        loading: false,
        result: null,
        err: typeof detail === 'string' ? detail : 'Please check your entries.',
      });
    }
  };

  return (
    <div
      data-testid="order-modal"
      role="dialog"
      aria-modal="true"
      className="fixed inset-0 z-[60] flex items-center justify-center p-4"
    >
      <div
        className="absolute inset-0 bg-[#7C9CB1]/40 backdrop-blur-sm"
        onClick={onClose}
        aria-hidden
      />
      <div className="relative w-full max-w-lg max-h-[92vh] overflow-y-auto thin-scroll bg-[#F4F8F9] border border-[#C1D7E2] shadow-2xl">
        <div className="flex items-start justify-between px-7 pt-7 pb-4 border-b border-[#C1D7E2]">
          <div>
            <p className="font-mono text-[10px] tracking-[0.3em] uppercase text-[#90B0BF]">
              Mock Checkout
            </p>
            <h3 className="font-serif text-2xl text-[#7C9CB1] font-light">
              Place your order
            </h3>
            {creatorHandle && (
              <p className="mt-1 text-sm font-light italic text-[#90B0BF]">
                Attributed to @{creatorHandle}
              </p>
            )}
          </div>
          <button
            onClick={onClose}
            data-testid="order-modal-close"
            aria-label="Close"
            className="text-[#7C9CB1] hover:text-[#90B0BF]"
          >
            <X size={18} strokeWidth={1.5} />
          </button>
        </div>

        {state.result ? (
          <div data-testid="order-success" className="p-7 space-y-4">
            <p className="font-serif italic text-2xl text-[#7C9CB1]">
              Order placed. The ritual is on its way.
            </p>
            <div className="font-mono text-xs text-[#7C9CB1] space-y-2 border border-[#C1D7E2] p-4">
              <Row l="Order ID" v={state.result.id.slice(0, 12) + '…'} />
              <Row l="Subtotal" v={`$${state.result.subtotal_usd.toFixed(2)}`} />
              <Row l="Buyer reward" v={`${state.result.buyer_seva.toFixed(2)} SEVA`} />
              <Row
                l="Creator reward"
                v={`${state.result.creator_seva.toFixed(2)} SEVA${
                  state.result.creator_handle
                    ? ` → @${state.result.creator_handle}`
                    : ''
                }`}
              />
              {state.result.ref_used && <Row l="Ref used" v={state.result.ref_used} />}
            </div>
            <button
              onClick={onClose}
              data-testid="order-success-close"
              className="bg-[#7C9CB1] text-[#F4F8F9] px-8 py-3 text-[11px] tracking-[0.22em] uppercase hover:bg-[#90B0BF] transition-colors"
            >
              Close
            </button>
          </div>
        ) : (
          <form onSubmit={onSubmit} className="p-7 space-y-5" data-testid="order-form">
            <Field label="Product">
              <select
                value={selectedSlug}
                onChange={(e) => setSelectedSlug(e.target.value)}
                data-testid="order-product-select"
                className="order-input"
                required
              >
                {products.map((p) => (
                  <option key={p.slug} value={p.slug}>
                    {p.name} — ${p.price_usd.toFixed(0)}
                  </option>
                ))}
              </select>
            </Field>
            <div className="grid grid-cols-2 gap-5">
              <Field label="Quantity">
                <input
                  type="number"
                  min={1}
                  max={20}
                  value={qty}
                  onChange={(e) => setQty(Number(e.target.value))}
                  data-testid="order-qty-input"
                  className="order-input"
                />
              </Field>
              <Field label="Subtotal">
                <p
                  data-testid="order-subtotal"
                  className="font-mono text-base text-[#7C9CB1] py-2"
                >
                  ${subtotal.toFixed(2)}
                </p>
              </Field>
            </div>
            <Field label="Your email">
              <input
                type="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                data-testid="order-email-input"
                className="order-input"
              />
            </Field>
            <Field label="Creator code or post ID (optional)">
              <input
                value={refInput}
                onChange={(e) => setRefInput(e.target.value)}
                data-testid="order-ref-input"
                placeholder="SEVA-MAYA-7C9C"
                className="order-input"
              />
            </Field>

            <div className="flex items-center justify-between gap-3 pt-2">
              <p className="font-mono text-[10px] tracking-[0.2em] text-[#90B0BF]">
                You earn {subtotal.toFixed(2)} SEVA · creator earns {(subtotal * 0.1).toFixed(2)} SEVA
              </p>
              <button
                type="submit"
                disabled={state.loading || !product}
                data-testid="order-submit"
                className="bg-[#7C9CB1] text-[#F4F8F9] px-8 py-3 text-[11px] tracking-[0.22em] uppercase hover:bg-[#90B0BF] transition-colors disabled:opacity-50"
              >
                {state.loading ? 'Placing…' : 'Place order'}
              </button>
            </div>
            {state.err && (
              <p data-testid="order-error" className="text-sm text-[#7C9CB1]">
                {state.err}
              </p>
            )}
          </form>
        )}
      </div>

      <style>{`
        .order-input {
          width: 100%;
          background: transparent;
          border: 0;
          border-bottom: 1px solid rgba(124,156,177,0.4);
          color: #7C9CB1;
          font-family: 'Outfit', sans-serif;
          font-weight: 300;
          padding: 10px 2px;
          font-size: 15px;
        }
        .order-input:focus { outline: none; border-bottom-color: #7C9CB1; }
      `}</style>
    </div>
  );
};

const Row = ({ l, v }) => (
  <div className="flex justify-between gap-3">
    <span className="text-[#90B0BF] tracking-[0.2em] uppercase text-[10px]">{l}</span>
    <span>{v}</span>
  </div>
);

const Field = ({ label, children }) => (
  <label className="block">
    <span className="font-mono text-[10px] tracking-[0.3em] uppercase text-[#90B0BF]">
      {label}
    </span>
    <div className="mt-2">{children}</div>
  </label>
);

export default OrderModal;
